import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Upload, Music, FileText, Copy, ArrowDown, ArrowUp } from "lucide-react";
import { TapTempoButton } from "./TapTempoButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FileSlot = ({ label, hint, icon: Icon, accent, accept, currentName, onPick, testId }) => {
  const inputRef = React.useRef(null);
  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className={`w-full text-left bg-[#121212] border-2 border-dashed rounded-xl p-4 flex items-center gap-4 transition-colors tap-lg ${
        accent === "neon"
          ? "border-[var(--gl-neon)]/40 hover:border-[var(--gl-neon)]"
          : "border-[var(--gl-gold)]/40 hover:border-[var(--gl-gold)]"
      }`}
      data-testid={testId}
    >
      <div
        className={`w-12 h-12 rounded-lg bg-black border flex items-center justify-center ${
          accent === "neon"
            ? "border-[var(--gl-neon)]/60 text-[var(--gl-neon)]"
            : "border-[var(--gl-gold)]/60 text-[var(--gl-gold)]"
        }`}
      >
        <Icon size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white tracking-wide">{label}</div>
        <div className="text-[11px] text-neutral-400 truncate">{hint}</div>
        <div className="text-xs text-neutral-500 truncate">
          {currentName || "Aucun fichier · appuyer pour choisir"}
        </div>
      </div>
      <Upload size={18} className="text-neutral-400" />
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </button>
  );
};

export const UploadDialog = ({ open, onOpenChange, song, onUpdated }) => {
  const [busy, setBusy] = React.useState(null);
  const [title, setTitle] = React.useState(song?.title || "");
  const [composer, setComposer] = React.useState(song?.composer || "");
  const [songKey, setSongKey] = React.useState(song?.key || "");
  const [bpm, setBpm] = React.useState(song?.bpm ? String(song.bpm) : "");
  const [notes, setNotes] = React.useState(song?.notes || "");
  const [savingMeta, setSavingMeta] = React.useState(false);

  React.useEffect(() => {
    setTitle(song?.title || "");
    setComposer(song?.composer || "");
    setSongKey(song?.key || "");
    setBpm(song?.bpm ? String(song.bpm) : "");
    setNotes(song?.notes || "");
  }, [song?.id, song?.title, song?.composer, song?.key, song?.bpm, song?.notes]);

  const upload = async (kind, file) => {
    if (!song) return;
    setBusy(kind);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/songs/${song.id}/upload/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated?.(data);
      toast.success(`Fichier chargé pour ${song.id}`);

      // Auto-suggest duplication if uploaded an audio variant and the other is empty
      if (kind === "audio_repete" && !data.audio_playback_filename) {
        toast(`Utiliser ce fichier aussi en PLAYBACK ?`, {
          duration: 8000,
          action: {
            label: "Oui, dupliquer",
            onClick: () => duplicate("repete", "playback"),
          },
        });
      } else if (kind === "audio_playback" && !data.audio_repete_filename) {
        toast(`Utiliser ce fichier aussi en RÉPÉTE ?`, {
          duration: 8000,
          action: {
            label: "Oui, dupliquer",
            onClick: () => duplicate("playback", "repete"),
          },
        });
      }
    } catch (e) {
      toast.error(`Échec upload ${kind}`);
    } finally {
      setBusy(null);
    }
  };

  const duplicate = async (source, target) => {
    if (!song) return;
    const tgtField = target === "repete" ? "audio_repete_filename" : "audio_playback_filename";
    if (song[tgtField]) {
      const labelTgt = target === "repete" ? "RÉPÉTE" : "PLAYBACK";
      if (!window.confirm(`Le fichier ${labelTgt} existant sera remplacé. Continuer ?`)) return;
    }
    setBusy(`dup-${source}-${target}`);
    try {
      const { data } = await axios.post(
        `${API}/songs/${song.id}/duplicate-audio`,
        null,
        { params: { source, target } }
      );
      onUpdated?.(data);
      toast.success(
        `${source === "repete" ? "Répéte" : "Playback"} → ${target === "repete" ? "Répéte" : "Playback"} dupliqué`
      );
    } catch (e) {
      toast.error("Échec de la duplication");
    } finally {
      setBusy(null);
    }
  };

  const saveMeta = async () => {
    if (!song) return;
    const payload = {};
    if (title !== song.title) payload.title = title;
    if ((composer || "") !== (song.composer || "")) payload.composer = composer;
    if ((songKey || "") !== (song.key || "")) payload.key = songKey;
    const bpmNum = parseFloat(bpm.replace(",", "."));
    if (!isNaN(bpmNum) && bpmNum !== song.bpm) payload.bpm = Math.max(20, Math.min(300, bpmNum));
    if ((notes || "") !== (song.notes || "")) payload.notes = notes;
    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }
    setSavingMeta(true);
    try {
      const { data } = await axios.patch(`${API}/songs/${song.id}`, payload);
      onUpdated?.(data);
      toast.success("Informations enregistrées");
      onOpenChange(false);
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0d0d0d] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto no-scrollbar"
        data-testid="upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-[var(--gl-gold)] font-display">
            Charger mes fichiers · {song?.id}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Édite les infos texte et importe les fichiers RÉPÉTE / PLAYBACK + paroles PDF.
          </DialogDescription>
        </DialogHeader>

        {/* Title + Composer (dual color) */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] font-bold">
              Titre du morceau
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Say Something"
              className="w-full bg-black border-2 border-[var(--gl-gold)]/50 rounded-lg px-3 py-3 text-lg font-bold text-[var(--gl-gold)] placeholder:text-[var(--gl-gold)]/30 focus:outline-none focus:border-[var(--gl-gold)] tap-lg"
              data-testid="meta-title-input"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--gl-green)] font-bold">
              Compositeur
            </label>
            <input
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Ex: Christina Aguilera"
              className="w-full bg-black border-2 border-[var(--gl-green)]/50 rounded-lg px-3 py-3 text-lg font-bold text-[var(--gl-green)] placeholder:text-[var(--gl-green)]/30 focus:outline-none focus:border-[var(--gl-green)] tap-lg"
              data-testid="meta-composer-input"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <p className="text-[11px] text-neutral-500 leading-snug">
            <span className="text-[var(--gl-gold)] font-semibold">Jaune</span> = titre ·{" "}
            <span className="text-[var(--gl-green)] font-semibold">Vert</span> = compositeur.
          </p>
        </div>

        {/* BPM + Tonality (free text) */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold">BPM</label>
              <input
                type="text"
                inputMode="decimal"
                value={bpm}
                onChange={(e) => setBpm(e.target.value)}
                placeholder="076.000"
                className="w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-lg font-bold text-white tabular-nums focus:outline-none focus:border-[var(--gl-gold)] tap-lg"
                data-testid="meta-bpm-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Tonalité</label>
              <input
                type="text"
                value={songKey}
                onChange={(e) => setSongKey(e.target.value)}
                placeholder="Sol, La m, Do…"
                className="w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-lg font-bold text-white focus:outline-none focus:border-[var(--gl-gold)] tap-lg"
                data-testid="meta-key-input"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
          <TapTempoButton onBpm={(b) => setBpm(String(b))} />
        </div>

        {/* Stage notes */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-4 space-y-2">
          <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold flex items-center gap-2">
            Notes de scène
            <span className="text-[10px] text-neutral-500 normal-case font-normal tracking-normal">
              (intro guitare 8 mesures, 2e couplet a cappella…)
            </span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Astuces / rappels qui te seront utiles pendant le concert."
            className="w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[var(--gl-gold)] resize-none leading-snug"
            data-testid="meta-notes-input"
            spellCheck={false}
          />
        </div>

        {/* File slots */}
        <div className="space-y-3 pt-1">
          <FileSlot
            label="RÉPÉTE (WAV / MP3)"
            hint={`Ex: ${song?.id} REPETE.WAV — pour les répétitions`}
            icon={Music}
            accent="gold"
            accept="audio/*"
            currentName={busy === "audio_repete" ? "Envoi en cours…" : song?.audio_repete_filename}
            onPick={(f) => upload("audio_repete", f)}
            testId="upload-audio-repete-btn"
          />

          {/* Duplicate buttons between the two audio variants */}
          <div className="flex items-center gap-2 px-1">
            <button
              type="button"
              onClick={() => duplicate("repete", "playback")}
              disabled={!song?.audio_repete_filename || busy === "dup-repete-playback"}
              className="flex-1 flex items-center justify-center gap-2 bg-[#121212] hover:bg-[#1a1a1a] border border-[var(--gl-gold)]/30 hover:border-[var(--gl-gold)]/70 disabled:opacity-30 disabled:cursor-not-allowed text-[var(--gl-gold)] rounded-lg py-2 px-3 text-xs font-bold uppercase tracking-wider transition-colors tap-lg"
              data-testid="duplicate-repete-to-playback-btn"
              title="Utiliser le même fichier pour Playback"
            >
              <Copy size={14} />
              <span>Répéte</span>
              <ArrowDown size={14} />
              <span>Playback</span>
            </button>
            <button
              type="button"
              onClick={() => duplicate("playback", "repete")}
              disabled={!song?.audio_playback_filename || busy === "dup-playback-repete"}
              className="flex-1 flex items-center justify-center gap-2 bg-[#121212] hover:bg-[#1a1a1a] border border-[var(--gl-neon)]/30 hover:border-[var(--gl-neon)]/70 disabled:opacity-30 disabled:cursor-not-allowed text-[var(--gl-neon)] rounded-lg py-2 px-3 text-xs font-bold uppercase tracking-wider transition-colors tap-lg"
              data-testid="duplicate-playback-to-repete-btn"
              title="Utiliser le même fichier pour Répéte"
            >
              <Copy size={14} />
              <span>Playback</span>
              <ArrowUp size={14} />
              <span>Répéte</span>
            </button>
          </div>

          <FileSlot
            label="PLAYBACK (MP3)"
            hint={`Ex: ${song?.id} PLAYBACK.MP3 — pour la scène`}
            icon={Music}
            accent="neon"
            accept="audio/*"
            currentName={busy === "audio_playback" ? "Envoi en cours…" : song?.audio_playback_filename}
            onPick={(f) => upload("audio_playback", f)}
            testId="upload-audio-playback-btn"
          />
          <FileSlot
            label="Paroles (PDF)"
            hint="PDF des paroles, affichées à l'écran"
            icon={FileText}
            accent="gold"
            accept="application/pdf"
            currentName={busy === "pdf" ? "Envoi en cours…" : song?.pdf_filename}
            onPick={(f) => upload("pdf", f)}
            testId="upload-pdf-btn"
          />
        </div>

        <button
          onClick={saveMeta}
          disabled={savingMeta}
          className="w-full tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider mt-2 disabled:opacity-50"
          data-testid="save-meta-btn"
        >
          {savingMeta ? "Enregistrement…" : "Enregistrer & fermer"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDialog;
